import { ReactNode } from 'react'

import { FormGroup } from './fields/FormGroup'
import { Table, TableColumn, TableSort } from './Table'
import styles from './DataTable.module.scss'

export const DATA_TABLE_LOCALE = {
  search: 'Buscar:',
  empty: 'Ningún dato disponible en esta tabla',
  noMatches: 'No se encontraron resultados',
  loading: 'Cargando...',
  previous: 'Anterior',
  next: 'Siguiente',
  info: (from: number, to: number, total: number) =>
    `Mostrando registros del ${from} al ${to} de un total de ${total} registros`,
} as const

const PAGE_WINDOW = 2

function _getVisiblePages(page: number, pageCount: number): readonly number[] {
  return Array.from({ length: pageCount }, (_, index) => index).filter(
    (index) => Math.abs(index - page) <= PAGE_WINDOW || index === 0 || index === pageCount - 1,
  )
}

function _getNumberedPagination(
  page: number,
  pageCount: number,
  onPageChange: (page: number) => void,
): ReactNode {
  const go = (event: React.MouseEvent, target: number) => {
    event.preventDefault()
    onPageChange(target)
  }

  return (
    <div className={`dataTables_paginate paging_simple_numbers ${styles.paginate}`}>
      <ul className="pagination">
        <li className={`paginate_button previous${page === 0 ? ' disabled' : ''}`}>
          <a href="#" onClick={(event) => go(event, Math.max(0, page - 1))}>
            {DATA_TABLE_LOCALE.previous}
          </a>
        </li>
        {_getVisiblePages(page, pageCount).map((index) => (
          <li key={index} className={`paginate_button${index === page ? ' active' : ''}`}>
            <a href="#" onClick={(event) => go(event, index)}>
              {index + 1}
            </a>
          </li>
        ))}
        <li className={`paginate_button next${page >= pageCount - 1 ? ' disabled' : ''}`}>
          <a href="#" onClick={(event) => go(event, Math.min(pageCount - 1, page + 1))}>
            {DATA_TABLE_LOCALE.next}
          </a>
        </li>
      </ul>
    </div>
  )
}

function _getSimplePagination(page: number, pageCount: number, onPageChange: (page: number) => void): ReactNode {
  return (
    <div className="dataTables_paginate">
      <button type="button" className="btn btn-link" disabled={page === 0} onClick={() => onPageChange(page - 1)}>
        {DATA_TABLE_LOCALE.previous}
      </button>
      <button
        type="button"
        className="btn btn-link"
        disabled={page >= pageCount - 1}
        onClick={() => onPageChange(page + 1)}
      >
        {DATA_TABLE_LOCALE.next}
      </button>
    </div>
  )
}

export function DataTable<Row>({
  columns,
  rows,
  getRowKey,
  id,
  search,
  onSearchChange,
  searchInputStyle,
  sort,
  onSort,
  page,
  pageCount,
  onPageChange,
  pagination = 'numbers',
  total,
  from,
  to,
  loading = false,
  emptyMessage = DATA_TABLE_LOCALE.empty,
  tableClassName,
  tableStyle,
  children,
}: {
  columns: readonly TableColumn<Row>[]
  rows: readonly Row[]
  getRowKey: (row: Row) => string | number
  id?: string
  search?: string
  onSearchChange?: (search: string) => void
  searchInputStyle?: React.CSSProperties
  sort?: TableSort
  onSort?: (key: string) => void
  page: number
  pageCount: number
  onPageChange: (page: number) => void
  pagination?: 'numbers' | 'simple'
  total: number
  from: number
  to: number
  loading?: boolean
  emptyMessage?: ReactNode
  tableClassName?: string
  tableStyle?: React.CSSProperties
  children?: ReactNode
}) {
  return (
    <div id={id} className="col-lg-12 no-padding-xs no-margin-xs">
      {children}
      {onSearchChange && (
        <div className={`dataTables_filter ${styles.filter}`}>
          <label>
            <i className="fa fa-search" /> {DATA_TABLE_LOCALE.search}{' '}
            <FormGroup className="form-group form-group-sm">
              <input
                type="search"
                className="form-control"
                style={searchInputStyle}
                value={search ?? ''}
                onChange={(event) => onSearchChange(event.target.value)}
              />
            </FormGroup>
          </label>
        </div>
      )}
      <Table
        columns={columns}
        rows={rows}
        getRowKey={getRowKey}
        sort={sort}
        onSort={onSort}
        loading={loading}
        loadingMessage={DATA_TABLE_LOCALE.loading}
        emptyMessage={emptyMessage}
        className={tableClassName}
        style={tableStyle}
      />
      <div className="dataTables_info" role="status">
        {DATA_TABLE_LOCALE.info(from, to, total)}
      </div>
      {pagination === 'numbers'
        ? _getNumberedPagination(page, pageCount, onPageChange)
        : pageCount > 1 && _getSimplePagination(page, pageCount, onPageChange)}
    </div>
  )
}
